import { useEffect, useState, type RefObject } from 'react';
import { calendarDaysBetween, type ChangeValue } from '@sl/core';
import type { StagedMarker } from '@sl/core/rows';
import { Banner, Button, Icon, Pill, VisuallyHidden, cx } from '../../ui';
import { useShell } from '../shell/shellContext';
import { testId } from '../testId';
import { STAGED_COPY, TRAY_COPY } from './copy';
import { useStaged } from './stagedContext';
import { useStagedNet } from './stagedNet';
import { useTray } from './StagedTray';
import styles from './StagedUi.module.css';

// THE THREE THINGS THE STAGED TRAY PUTS ON A SCREEN IT DOES NOT OWN (T-129; REQ-101 AC1, AC4, AC5).
//
// They share a file because they share one idea and one stylesheet: **a staged value is drawn, never
// applied.** `components.html` row 8 is the marker (the new value, the old one struck through, the teal
// dash that means "with the staged changes") and row 17 is the pill. Nothing here decides anything — the
// open change-set is read once at the shell (`stagedContext.tsx`) and these render what it says.
//
//   · `StagedMark`      one staged value on a row or a header. AC1.
//   · `StagedPill`      "3 staged" in the top bar, on every screen. AC4.
//   · `StagedReminder`  one gentle band on the next visit, when the oldest has waited a day. AC5.

// ---- the marker (REQ-101 AC1; components.html row 8) ------------------------------------------------------

export interface StagedMarkProps {
  marker: StagedMarker;
  /** The live value and the staged one, already formatted by the caller — the money context is the row's. */
  from: string;
  to: string;
  /**
   * The root the mark hangs off: `<root>.staged`, with the struck-through original at
   * `<root>.staged.original`.
   *
   * **A STATUS marker takes the ROW's root and a BUDGET marker takes the budget cell's**
   * (`row.adset.cart-abandoners-14d.staged` and `row.adset.cart-abandoners-14d.budget.staged`). One
   * entity can carry both at once — API §6.3 upserts on `(entityId, field)`, not on the entity — and two
   * elements under one id is the defect TEST_PLAN §1.4 forbids and BUG-051 was filed for.
   */
  testID: string;
  className?: string;
}

/**
 * The staged value with the original struck through beside it: "$30 ~~$25~~".
 *
 * **It does not replace the row's number, it sits with it** — an entity staged off is still running and
 * still spending until someone publishes, so the two values are shown together and the accessible name
 * says which is which and that it has not happened. The teal dash is the design system's mark for "with
 * the staged changes" (§4.1 / §12), and it is a glyph rather than colour alone.
 */
export function StagedMark({ marker, from, to, testID, className }: StagedMarkProps) {
  const { account } = useShell();
  // BUG-099 (§12 no. 9, D-209): the ONE pill for a change that is not published yet — dashed teal, the clock and
  // the word ("Planned" in View-only, §12.2) — then the new value with the old struck through. The phone's
  // `StagedTag` drew it already; the web cell said it only in a `title`. The pill is decoration for a screen
  // reader, whose sentence is `markerLabel` below.
  const word = account.capabilities.canPublish ? STAGED_COPY.stagedWord : STAGED_COPY.plannedWord;
  return (
    <span
      className={cx(styles.mark, className)}
      data-testid={testId(testID, 'staged')}
      data-staged={marker.field}
      data-staged-item={marker.itemId}
    >
      <span
        aria-hidden="true"
        className={styles.markPill}
        data-testid={testId(testID, 'staged', 'pill')}
      >
        <Pill tone="staged" icon="clock" size="sm">
          {word}
        </Pill>
      </span>
      <Icon name="minus" size={14} className={styles.dash} />
      <span className={styles.to}>{to}</span>
      <s className={styles.from} data-testid={testId(testID, 'staged', 'original')}>
        {from}
      </s>
      <VisuallyHidden>{STAGED_COPY.markerLabel(to, from)}</VisuallyHidden>
    </span>
  );
}

/** The words for a `ChangeValue` that is a switch; an amount is the caller's formatter. */
export function statusWords(value: ChangeValue): string | null {
  return value.kind === 'status'
    ? value.isOn
      ? STAGED_COPY.statusOn
      : STAGED_COPY.statusOff
    : null;
}

// ---- the pill (REQ-101 AC4; components.html row 17) --------------------------------------------------------

/**
 * "3 staged", beside the mode chip, on every screen the shell draws — REQ-101 AC4's "the count is visible
 * wherever you are". Hidden at zero: a pill reading "0 staged" is a control with nothing behind it.
 *
 * **T-132 made it a door.** T-129 drew it `aria-disabled` with "the review tray arrives with the next
 * screen" in its tooltip, because DESIGN_SYSTEM §10 says the MVP never shows a dead control; the tray is
 * here now, so the pill is a real button that opens it, and the panel hangs off this element.
 *
 * The pill keeps its ref in the tray's own context, so the reminder band can open the same panel against
 * the same anchor and the focus has one place to come back to.
 */
export function StagedPill() {
  const { count, isPending } = useStaged();
  const { open, setOpen, anchorRef } = useTray();
  // BUG-098 (w07 ①): the set's net effect on daily spend beside the count, where it fits (below 1400 the
  // stylesheet drops it, in the top bar's own yield order — D-496); the button's own text stays the count.
  const net = useStagedNet();
  if (isPending || count === 0) return null;
  const one = count === 1;
  const n = String(count);
  return (
    // T-076-W4's shape, the one the mode chip already uses: the BUTTON carries the id and the ARIA — QA
    // reads `shell.staged-pill`'s text and presses it — and the pill inside is the face, with no id of its
    // own, so one id names exactly one element.
    <>
      <button
        ref={anchorRef as RefObject<HTMLButtonElement>}
        type="button"
        className={styles.pillButton}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={STAGED_COPY.pillLabel(n, one)}
        data-testid={testId('shell', 'staged-pill')}
        data-count={count}
        onClick={() => setOpen(!open)}
      >
        <Pill tone="staged" icon="clock" size="sm">
          {STAGED_COPY.pill(n, one)}
        </Pill>
      </button>
      {net !== null && net.amount !== 0 ? (
        <span className={styles.pillNet} data-testid={testId('shell', 'staged-pill', 'net')}>
          {STAGED_COPY.pillNet(net.text)}
        </span>
      ) : null}
    </>
  );
}

// ---- the 24-hour reminder (REQ-101 AC5) ---------------------------------------------------------------------

/**
 * One dismissible band, on the next visit, when the oldest staged change has been waiting more than a day.
 *
 * **It is a reminder, not a warning.** Nothing has expired, nothing was sent, and nothing happens on its
 * own — so it is the `info` tone, it names the count and the age, and "Not now" makes it go away for this
 * visit. The clock is the app's injected one (D-020), so `?demoNow=` moves it: at DEMO_NOW the seed's three
 * items are minutes old and the band is correctly absent, and `?demoScenario=stale-staged` is what makes it
 * reachable (the oldest item staged at 6:24 am two days back — `STALE_STAGED_HOUR`, D-911).
 *
 * **Two clocks, on purpose** (BUG-203): whether to show the band at all is REQ-101 AC5's 24 HOURS, a
 * duration; the age it then says is a count of CALENDAR days in the ad account's zone.
 */
export function StagedReminder({ now, timeZone }: { now: number; timeZone: string }) {
  const { count, oldestStagedAt } = useStaged();
  const { setOpen } = useTray();
  const [dismissed, setDismissed] = useState(false);
  const days = oldestStagedAt === null ? 0 : wholeDaysSince(oldestStagedAt, now, timeZone);

  // A different oldest item is a different reminder: staging something new does not un-dismiss it, but
  // publishing everything and starting again does.
  useEffect(() => setDismissed(false), [oldestStagedAt]);

  // `days < 1` is a belt, not the gate: more than 24 hours always crosses a midnight, so a due reminder
  // always has at least one calendar day to name. It keeps "waiting 0 days" off the screen if it ever does.
  if (dismissed || count === 0 || !reminderIsDue(oldestStagedAt, now) || days < 1) return null;
  const one = count === 1;
  return (
    <Banner
      tone="info"
      icon="clock"
      title={STAGED_COPY.reminderTitle(String(count), one)}
      testID={testId('staged', 'reminder')}
      actions={
        <>
          <Button
            variant="secondary"
            size="sm"
            testID={testId('staged', 'reminder', 'dismiss')}
            onClick={() => setDismissed(true)}
          >
            {STAGED_COPY.reminderDismiss}
          </Button>
          {/* T-132 — the band is the tray's other way in: a reminder that names a count and then leaves the
              reader to find the pill is half a reminder. It opens the same panel against the same anchor. */}
          <Button
            variant="primary"
            size="sm"
            testID={testId('staged', 'reminder', 'open')}
            onClick={() => setOpen(true)}
          >
            {TRAY_COPY.title}
          </Button>
        </>
      }
    >
      {STAGED_COPY.reminderBody(STAGED_COPY.reminderAgeDays(days))}
    </Banner>
  );
}

/**
 * REQ-101 AC5's own threshold: the band is for a change left unpublished for **more than 24 hours**. That
 * is a DURATION, and a duration carries no zone — the same argument D-870 uses to exempt instants under six
 * hours old — so it is counted in elapsed milliseconds, while the age it then SAYS is a calendar count.
 * Keeping the two apart is what stops something staged at 11:50 pm from being announced as "waiting a day"
 * twenty minutes later.
 */
const REMINDER_AFTER_MS = 86_400_000;

/** Has the oldest staged change gone unpublished for longer than REQ-101 AC5's 24 hours? */
export function reminderIsDue(oldestStagedAt: string | null, now: number): boolean {
  if (oldestStagedAt === null) return false;
  const then = Date.parse(oldestStagedAt);
  return !Number.isNaN(then) && now - then > REMINDER_AFTER_MS;
}

/**
 * Whole CALENDAR days from an instant to now, counted in the **ad account's** zone — the count the inbox's
 * seven-day membership uses (ENGINE §10.7; D-912), never elapsed hours. The zone is the ACCOUNT's, not the
 * reader's, because a staged change belongs to the account: it was made against that account's day and it
 * waits in that account's calendar. (An alert's age goes the other way — the ladder counts in the DEVICE's
 * zone, REQ-261 AC4, because that age is about when the person is looking.)
 *
 * **Elapsed hours were BUG-203.** `Math.floor(ms / 86,400,000)` read the demo's stale item — staged at
 * 6:24 am two days back (`STALE_STAGED_HOUR`, D-911), so 42 h old at DEMO_NOW — as "a day", where the story
 * and the phone's own test say two. D-870 wrote the trap down before anyone fell into it: the day counts are
 * calendar-day differences, and **a surface that counts hours is the bug**. The count is core's
 * `calendarDaysBetween` — the one copy of that rule (T-363) — which cannot flip with the hour of the day, and
 * on which a DST day is still one day.
 *
 * A negative age and an unparseable instant both answer 0, so a clock skew or a bad record leaves the band
 * off rather than announcing that something has been waiting -1 days.
 */
export function wholeDaysSince(instant: string, now: number, timeZone: string): number {
  const then = Date.parse(instant);
  if (Number.isNaN(then)) return 0;
  const days = calendarDaysBetween(then, now, timeZone);
  return days > 0 ? days : 0;
}
